import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Menu } from '@core/data/menu.data';
import { TokenInformation } from '@core/models/auth.model';
import { MenuItem, MenuSection } from '@core/models/menu.model';
import { PermissionsService } from '@core/services/permissions/permissions.service';
import { SessionService } from '@core/services/session/session.service';
import { AppMenuitem } from './app.menuitem';
import { ClientConfigService } from '../service/client-config/client-config.service';
import {
  menuClient,
  menuClientPersonalDetail,
} from '@modules/clients/domain/data/menu-client.data';
import { jwtDecode } from 'jwt-decode';

/**
 * Separa segmentos que contengan barras internas para que Angular Router
 * los interprete correctamente. Ej: "shipments/list" → ["shipments", "list"]
 */
function splitSegments(segments: string[]): string[] {
  return segments.flatMap((s) => s.split('/').filter(Boolean));
}

/**
 * Prefija recursivamente los routerLink relativos de un árbol de MenuItem.
 * Si el primer segmento empieza con "/", la ruta es absoluta y queda intacta.
 */
function prefixMenuLinks(items: MenuItem[], prefix: string[]): MenuItem[] {
  return items.map((item) => {
    const prefixed: MenuItem = { ...item };
    if (item.routerLink?.length) {
      const [firstSegment] = item.routerLink;
      prefixed.routerLink =
        typeof firstSegment === 'string' && firstSegment.startsWith('/')
          ? item.routerLink
          : [...prefix, ...splitSegments(item.routerLink)];
    }
    if (item.items?.length) {
      prefixed.items = prefixMenuLinks(item.items, prefix);
    }
    return prefixed;
  });
}

/** Convierte menuClientPersonalDetail (MenuItem[]) en MenuSection[] con rutas absolutas al cliente. */
function buildClientScopeMenu(clientId: number, permissions: PermissionsService): MenuSection[] {
  const prefix = ['/clients/detail', String(clientId)];

  const model = permissions.filterMenuByPermissions(menuClientPersonalDetail);
  return prefixMenuLinks(model, prefix) as MenuSection[];
}

@Component({
  selector: '[app-menu]',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule],
  template: `
    @if (permissionsService.permissionsError()) {
      <div class="menu-server-unavailable">
        <i class="pi pi-server" aria-hidden="true"></i>
        <span>Servidor no disponible</span>
      </div>
    } @else {
      <ul class="layout-menu">
        @for (item of menuUser(); track $index) {
          @if (!item.separator) {
            <li app-menuitem [item]="item" [index]="$index" [root]="true" menuId="main"></li>
          } @else {
            <li class="menu-separator"></li>
          }
        }
      </ul>
    }
  `,
})
export class AppMenu {
  protected readonly permissionsService = inject(PermissionsService);
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly sessionService = inject(SessionService);

  // Reactivo: se recalcula cuando cambian permisos, config o sesión.
  menuUser = computed(() => {
    const clientId = this._clientScopeId();

    // En modo cliente-scope: usar menuClientPersonalDetail con rutas absolutas al cliente.
    if (clientId !== null) {
      return buildClientScopeMenu(clientId, this.permissionsService);
    }

    const model = this.clientConfigService.configSignal() ? menuClient : Menu;
    return this.permissionsService.filterMenuByPermissions(model);
  });

  /**
   * Retorna el clientId activo si el usuario es "cliente-scope" (sin create_client y
   * con al menos un internal_client). Retorna null si es CEO/admin normal.
   */
  _clientScopeId(): number | null {
    if (this.permissionsService.hasPermission('create_client')) {
      return null;
    }
    const session = this.sessionService.session();
    if (!session?.current_client_id) return null;
    try {
      const decoded = jwtDecode<TokenInformation>(session.token);
      if ((decoded.internal_client_ids?.length ?? 0) === 0) return null;
      return session.current_client_id;
    } catch {
      return null;
    }
  }
}
